import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getCustomers } from '../../services/adminService';
import CustomerNoticeModal from '../../components/CustomerNoticeModal';

const formatDate = (iso) =>
  new Date(iso).toLocaleDateString('es-MX', { dateStyle: 'medium' });

const inputStyle = {
  padding: '0.5rem 0.75rem',
  border: '1px solid #ddd',
  borderRadius: '8px',
  fontSize: '0.9rem',
  outline: 'none',
};

const PAGE_LIMIT = 20;

const AdminCustomerList = () => {
  const { session } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [noticeCustomer, setNoticeCustomer] = useState(null);
  // Notice to someone who isn't in the list (no customer → modal asks for the address)
  const [noticeOpen, setNoticeOpen] = useState(false);
  const [sentTo, setSentTo] = useState(null);

  // Debounce search 400 ms
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    if (!session?.access_token) {
      setLoading(false);
      setError('No hay sesión activa.');
      return;
    }
    let cancelled = false;
    setLoading(true);

    const params = { page, limit: PAGE_LIMIT };
    if (debouncedSearch) params.search = debouncedSearch;

    getCustomers(session.access_token, params)
      .then(data => {
        if (cancelled) return;
        setCustomers(data.customers || []);
        setTotal(data.total || 0);
        setTotalPages(data.totalPages || 1);
        setLoading(false);
      })
      .catch(err => {
        if (cancelled) return;
        setError(err.message);
        setLoading(false);
      });

    return () => { cancelled = true; };
  }, [session?.access_token, page, debouncedSearch]);

  return (
    <div className="container" style={{ padding: '2rem 0 4rem' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.5rem', flexWrap: 'wrap' }}>
        <Link to="/admin" style={{ color: '#888', textDecoration: 'none', fontSize: '0.9rem' }}>← Panel</Link>
        <h1 style={{ fontFamily: "'Gambado Sans', sans-serif", fontSize: '1.6rem', color: '#333', margin: 0 }}>
          Clientes
        </h1>
        <span style={{ color: '#888', fontSize: '0.9rem', marginLeft: 'auto' }}>
          {total} cliente{total !== 1 ? 's' : ''}
        </span>
        <button
          type="button"
          onClick={() => { setSentTo(null); setNoticeOpen(true); }}
          style={{ padding: '0.45rem 0.9rem', borderRadius: '8px', border: '1px solid #ddd', background: 'white', color: '#333', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer' }}
        >
          Correo a otra persona
        </button>
      </div>

      {/* Search */}
      <div style={{ margin: '1.5rem 0' }}>
        <input
          type="text"
          placeholder="Buscar por email o nombre..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ ...inputStyle, width: '100%', maxWidth: '360px', boxSizing: 'border-box' }}
        />
      </div>

      {/* Error */}
      {error && <p style={{ color: '#dc3545', marginBottom: '1rem' }}>{error}</p>}
      {sentTo && <p role="status" style={{ color: '#28a745', marginBottom: '1rem' }}>Correo enviado a {sentTo}.</p>}

      {/* Table */}
      <div style={{ background: 'white', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
            <thead>
              <tr style={{ background: '#fafafa' }}>
                {['Email', 'Nombre', 'Teléfono', 'Instagram', 'Ciudad', 'Registro', ''].map(h => (
                  <th key={h} style={{ padding: '0.75rem 1rem', textAlign: 'left', color: '#666', fontWeight: '600', whiteSpace: 'nowrap', borderBottom: '1px solid #f0f0f0' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} style={{ padding: '2.5rem', textAlign: 'center', color: '#888' }}>Cargando...</td>
                </tr>
              ) : customers.length === 0 ? (
                <tr>
                  <td colSpan={7} style={{ padding: '2.5rem', textAlign: 'center', color: '#888' }}>
                    No se encontraron clientes.
                  </td>
                </tr>
              ) : customers.map(c => (
                <tr key={c.id} style={{ borderBottom: '1px solid #f8f8f8' }}>
                  <td style={{ padding: '0.75rem 1rem', color: '#333' }}>{c.email}</td>
                  <td style={{ padding: '0.75rem 1rem', fontWeight: '500', color: '#333' }}>{c.name || '—'}</td>
                  <td style={{ padding: '0.75rem 1rem', color: '#555' }}>{c.phone || '—'}</td>
                  <td style={{ padding: '0.75rem 1rem', color: '#555' }}>
                    {c.instagram ? (
                      <a href={`https://www.instagram.com/${c.instagram}/`} target="_blank" rel="noopener noreferrer" style={{ color: '#2ca9e1', textDecoration: 'none' }}>
                        @{c.instagram}
                      </a>
                    ) : '—'}
                  </td>
                  <td style={{ padding: '0.75rem 1rem', color: '#555' }}>{c.city || '—'}</td>
                  <td style={{ padding: '0.75rem 1rem', color: '#888', whiteSpace: 'nowrap' }}>
                    {c.created_at ? formatDate(c.created_at) : '—'}
                  </td>
                  <td style={{ padding: '0.75rem 1rem', whiteSpace: 'nowrap' }}>
                    {c.email && (
                      <button
                        type="button"
                        onClick={() => { setSentTo(null); setNoticeCustomer(c); }}
                        aria-label={`Enviar correo a ${c.email}`}
                        style={{ padding: '0.35rem 0.75rem', border: '1px solid #ddd', borderRadius: '8px', background: 'white', color: '#333', cursor: 'pointer', fontSize: '0.85rem' }}
                      >
                        Enviar correo
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginTop: '1.5rem', justifyContent: 'center' }}>
          <button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
            style={{
              padding: '0.5rem 1rem',
              border: '1px solid #ddd',
              borderRadius: '8px',
              background: page === 1 ? '#f8f9fa' : 'white',
              color: page === 1 ? '#aaa' : '#333',
              cursor: page === 1 ? 'default' : 'pointer',
              fontSize: '0.9rem',
            }}
          >
            ← Anterior
          </button>
          <span style={{ color: '#666', fontSize: '0.9rem' }}>
            Página {page} de {totalPages}
          </span>
          <button
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            style={{
              padding: '0.5rem 1rem',
              border: '1px solid #ddd',
              borderRadius: '8px',
              background: page === totalPages ? '#f8f9fa' : 'white',
              color: page === totalPages ? '#aaa' : '#333',
              cursor: page === totalPages ? 'default' : 'pointer',
              fontSize: '0.9rem',
            }}
          >
            Siguiente →
          </button>
        </div>
      )}

      {(noticeCustomer || noticeOpen) && (
        <CustomerNoticeModal
          customer={noticeCustomer}
          accessToken={session?.access_token}
          onClose={() => { setNoticeCustomer(null); setNoticeOpen(false); }}
          onSent={(c) => { setNoticeCustomer(null); setNoticeOpen(false); setSentTo(c.email); }}
        />
      )}
    </div>
  );
};

export default AdminCustomerList;
